import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPatient } from '../../../../../../common/interfaces/patient';
import type { IReduxState } from '../../../../../configureStore';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import type { IDermaOthV1RequestCustomControlData } from './DermaOthV1RequestAddEdit';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { CustomControlStatus } from '../../../../../../common/enums/customControlStatus';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { YesOrNo } from '../../../../../../common/enums/yesOrNo';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { ResendInvitationButton } from '../../../../elements/carePathButtons/ResendInvitationButton';
import { FormParagraph, InlineElement, LinkOnClick, UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ResendInviteModal } from '../../../../elements/ResendInviteModal';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { DermaOthV1FollowingSteps } from './DermaOthV1FollowingSteps';

export function DermaOthV1RequestRead(props: ICustomControlRequestReadProps) {
    const [procedureIsWithPatient, setProcedureIsWithPatient] = useState(false);
    const [canEditEmail, setCanEditEmail] = useState(false);
    const currentUserId = useSelector((state: IReduxState) => state.authentication.id);
    const [showEditEmailModal, setShowEditEmailModal] = useState(false);
    const [patient, setPatient] = useState<IPatient>();

    useEffect(() => {
        loadProcedure();
        loadPatient();
    }, [props.request]);

    const customControlData: IDermaOthV1RequestCustomControlData = props.request.customControlData;
    if (!customControlData) {
        return null;
    }

    const isAltoProcedure =
        procedureIsWithPatient &&
        customControlData.patientHasDigid === YesOrNo.no &&
        customControlData.invitationSentAt;

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isExaminationRequest
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => {
                    await props.updateItemState({ ...props.itemState, isOpen: isOpened });
                }}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            {customControlData.contactgegevens_patient.emailadres && (
                <HorizontalFormGroupRead>
                    <FormLabel>Thuisconsult verstuurd naar</FormLabel>
                    <InputContainer>
                        {canEditEmail && (
                            <FormParagraph>
                                <InlineElement className="mr-3">
                                    {customControlData.contactgegevens_patient.emailadres}
                                </InlineElement>
                                {isAltoProcedure && (
                                    <LinkOnClick
                                        className="btn-transparent"
                                        onClick={() => setShowEditEmailModal(true)}
                                    >
                                        Verstuur naar ander e-mailadres
                                    </LinkOnClick>
                                )}
                            </FormParagraph>
                        )}
                        {!canEditEmail && (
                            <UserTextParagraph>
                                {customControlData.contactgegevens_patient.emailadres}
                            </UserTextParagraph>
                        )}
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {customControlData.patientHasDigid && (
                <HorizontalFormGroupRead>
                    <FormLabel>Heeft de patiënt een DigiD?</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>
                            {customControlData.patientHasDigid === YesOrNo.yes
                                ? translateKey('common.yes')
                                : translateKey('common.no')}
                        </UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {isAltoProcedure && showInvitationMessage(customControlData.invitationSentAt!)}

            <DermaOthV1FollowingSteps />

            <RequestReadButtons
                {...props}
                additionalButtons={getButtons()}
            />

            {patient && showEditEmailModal && isAltoProcedure && (
                <ResendInviteModal
                    onClose={async () => {
                        setShowEditEmailModal(false);
                        await props.reloadRequest();
                    }}
                    patient={patient}
                    request={props.request}
                />
            )}
        </ProcessContainer>
    );

    function getButtons() {
        if (patient && procedureIsWithPatient && customControlData.patientHasDigid === YesOrNo.no) {
            return [
                <ResendInvitationButton
                    patient={patient}
                    reloadRequest={props.reloadRequest}
                    request={props.request}
                />,
            ];
        }

        return [];
    }

    function showInvitationMessage(invitationSentAt: string | Date) {
        const remainingHours = 24 - Math.trunc(DateTime.now().diff(DateTime.from(invitationSentAt), 'hours'));
        if (remainingHours > 0) {
            return (
                <Alert appearance="success">
                    De link die we naar de patiënt hebben gestuurd blijft nog {remainingHours} uur geldig
                </Alert>
            );
        } else {
            return (
                <Alert appearance="warning">
                    De link die we naar de patiënt hebben gestuurd is verlopen. Verzend de aanvraag opnieuw om de
                    patiënt een nieuwe link te sturen. Of stuur de uitnodiging naar een ander e-mailadres via de
                    bovenstaande link.
                </Alert>
            );
        }
    }

    async function loadProcedure() {
        if (props.request.status !== RequestStatus.draft) {
            const result = await getJson(`/api/requests/${props.request.id}/procedure`);
            const withPatient =
                props.request.status === RequestStatus.active &&
                result.json?.customControlStatus === CustomControlStatus.withPatient;
            setProcedureIsWithPatient(withPatient);
            setCanEditEmail(withPatient && currentUserId === props.request.requesterPractitionerId);
        } else {
            setProcedureIsWithPatient(false);
            setCanEditEmail(false);
        }
    }

    async function loadPatient() {
        const url = `/api/patients/${props.request.patientId}`;
        const result = await getJson(url);
        setPatient(result.json);
    }
}
